import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Camera,
  CheckCircle2,
  Globe2,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import styled, { createGlobalStyle } from "styled-components";
import { MARKETPLACE_FONTS } from "./MarketplaceConstants";
import teamsImg from "../../../assets/images/marketplace/teams.webp";
import marketplaceHomeBg from "../../../assets/images/marketplace/marketplace_home_bg.webp";
import team5Img from "../../../assets/images/marketplace/team5.webp";

const Fonts = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@500;600;700;800&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');
`;

const Page = styled.main`
  min-height: 100vh;
  background: #0d1012;
  color: var(--mp-headingColor);
  font-family: "Inter", ${MARKETPLACE_FONTS.SANS};

  [data-marketplace-theme="light"] & {
    background: #f6f7f4;
  }
`;

const Hero = styled.section`
  position: relative;
  min-height: 430px;
  overflow: hidden;
  display: flex;
  align-items: center;
  padding: 28px 0 38px;
  background: #0d1012;

  [data-marketplace-theme="light"] & {
    background: #f6f7f4;
  }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 28px 0 auto;
  left: 50%;
  width: min(920px, calc(100% - 48px));
  height: 345px;
  transform: translateX(-50%);
  object-fit: cover;
  border-radius: 4px;
  opacity: 0.5;

  [data-marketplace-theme="light"] & {
    opacity: 0.34;
  }
`;

const Shade = styled.div`
  position: absolute;
  inset: 28px 0 auto;
  left: 50%;
  width: min(920px, calc(100% - 48px));
  height: 345px;
  transform: translateX(-50%);
  border-radius: 4px;
  background:
    radial-gradient(circle at 72% 48%, rgba(201,77,170,0.32), transparent 28%),
    linear-gradient(90deg, rgba(10,12,14,0.82), rgba(10,12,14,0.42)),
    linear-gradient(0deg, rgba(10,12,14,0.58), rgba(10,12,14,0.22));

  [data-marketplace-theme="light"] & {
    background:
      radial-gradient(circle at 72% 34%, rgba(216,99,186,0.18), transparent 32%),
      linear-gradient(90deg, rgba(247,248,252,0.96), rgba(247,248,252,0.56)),
      linear-gradient(0deg, var(--mp-bodyBackground), rgba(247,248,252,0) 38%);
  }
`;

const Container = styled.div`
  width: min(920px, calc(100% - 48px));
  margin: 0 auto;
  position: relative;
  z-index: 1;

  @media (max-width: 720px) {
    width: min(100% - 32px, 920px);
  }
`;

const Eyebrow = styled.div`
  color: #ffdea1;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0 auto 12px;
  justify-content: center;
`;

const Title = styled.h1`
  max-width: 440px;
  margin: 0 auto 10px;
  color: var(--mp-heroHeadingColor);
  font-family: "Plus Jakarta Sans", ${MARKETPLACE_FONTS.SANS};
  font-size: clamp(25px, 4vw, 38px);
  line-height: 1.05;
  text-align: center;

  span {
    color: #ffdea1;
  }
`;

const Lead = styled.p`
  max-width: 390px;
  margin: 0 auto;
  color: var(--mp-heroSubColor);
  font-size: 10px;
  line-height: 16px;
  text-align: center;
`;

const HeroActions = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 18px;
`;

const Button = styled.button<{ $primary?: boolean }>`
  min-height: 28px;
  border: 1px solid ${p => (p.$primary ? "transparent" : "var(--mp-sidebarBorder)")};
  border-radius: 999px;
  background: ${p => (p.$primary ? "var(--mp-heroBtnBg)" : "color-mix(in srgb, var(--mp-cardBackground) 84%, transparent)")};
  color: ${p => (p.$primary ? "#24180c" : "var(--mp-headingColor)")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  cursor: pointer;
  padding: 0 16px;
  font: 800 10px/14px "Inter", ${MARKETPLACE_FONTS.SANS};
  backdrop-filter: blur(12px);
`;

const Section = styled.section`
  padding: 42px 0;
`;

const Split = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 390px;
  gap: 44px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 12px;
  color: var(--mp-headingColor);
  font-family: "Plus Jakarta Sans", ${MARKETPLACE_FONTS.SANS};
  font-size: clamp(22px, 3vw, 30px);
  line-height: 1.1;
`;

const Body = styled.p`
  margin: 0 0 12px;
  color: var(--mp-subColor);
  font-size: 11px;
  line-height: 18px;
`;

const ImageStack = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.72fr;
  gap: 10px;
  align-items: end;
`;

const ImageCard = styled.img<{ $small?: boolean }>`
  width: 100%;
  height: ${p => (p.$small ? "170px" : "245px")};
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 24px 58px rgba(0,0,0,0.22);
`;

const ValueGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 28px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.article`
  border: 1px solid var(--mp-sidebarBorder);
  border-radius: 12px;
  background: var(--mp-cardBackground);
  padding: 16px;
`;

const IconWrap = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  color: #24180c;
  background: var(--mp-heroBtnBg);
  margin-bottom: 12px;
`;

const ValueTitle = styled.h3`
  margin: 0 0 8px;
  color: var(--mp-headingColor);
  font-size: 12px;
  line-height: 16px;
`;

const ValueText = styled.p`
  margin: 0;
  color: var(--mp-subColor);
  font-size: 10px;
  line-height: 16px;
`;

const StatsBand = styled.section`
  border-top: 1px solid var(--mp-sidebarBorder);
  border-bottom: 1px solid var(--mp-sidebarBorder);
  background: color-mix(in srgb, var(--mp-cardBackground) 72%, var(--mp-bodyBackground));
  padding: 24px 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Stat = styled.div`
  text-align: center;
`;

const StatValue = styled.strong`
  display: block;
  color: var(--mp-headingColor);
  font-size: 22px;
  line-height: 28px;
`;

const StatLabel = styled.span`
  color: var(--mp-subColor);
  font-size: 10px;
`;

const values = [
  { icon: <Camera size={19} />, title: "Production Ready", text: "Verified gear, crews, studios, and agencies ready for real project timelines." },
  { icon: <Users size={19} />, title: "Creator First", text: "Tools for photographers, filmmakers, editors, and studios to grow together." },
  { icon: <ShieldCheck size={19} />, title: "Trusted Exchange", text: "Cleaner discovery, safer bookings, and stronger signals before a deal begins." },
  { icon: <Globe2 size={19} />, title: "Local to Global", text: "A marketplace built for city-level availability and cross-region creative scale." },
];

const MarketplaceAboutUsPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Page>
      <Fonts />
      <Hero>
        <HeroImg src={teamsImg} alt="KwikMemories production workspace" />
        <Shade />
        <Container>
          <Eyebrow><Sparkles size={14} /> About KwikMemories</Eyebrow>
          <Title>Empowering Creators. <span>Democratizing Production.</span></Title>
          <Lead>
            KwikMemories brings photographers, filmmakers, agencies, production
            crews, and equipment owners into one connected marketplace built for
            high-quality creative work.
          </Lead>
          <HeroActions>
            <Button $primary onClick={() => navigate("/marketplace/agency-directory")}>
              Join the Ecosystem <ArrowRight size={15} />
            </Button>
          </HeroActions>
        </Container>
      </Hero>

      <Section>
        <Container>
          <Split>
            <div>
              <SectionTitle>Breaking down barriers to high-fidelity creation.</SectionTitle>
              <Body>
                Production can be fragmented: talent in one channel, gear in
                another, agencies somewhere else, and trust scattered across
                messages. KwikMemories brings those moving parts into a single
                marketplace where creative teams can compare, connect, and move
                from brief to booking with more confidence.
              </Body>
              <Body>
                We are building for the people who make memories tangible: the
                studios covering milestone events, the crews chasing impossible
                shots, and the agencies shaping campaigns that need polish from
                day one.
              </Body>
            </div>
            <ImageStack>
              <ImageCard src={marketplaceHomeBg} alt="Creative team on set" />
              <ImageCard $small src={team5Img} alt="Production crew member" />
            </ImageStack>
          </Split>

          <ValueGrid>
            {values.map(item => (
              <ValueCard key={item.title}>
                <IconWrap>{item.icon}</IconWrap>
                <ValueTitle>{item.title}</ValueTitle>
                <ValueText>{item.text}</ValueText>
              </ValueCard>
            ))}
          </ValueGrid>
        </Container>
      </Section>

      <StatsBand>
        <Container>
          <StatsGrid>
            <Stat><StatValue>10k+</StatValue><StatLabel>Creative moments powered</StatLabel></Stat>
            <Stat><StatValue>500+</StatValue><StatLabel>Marketplace professionals</StatLabel></Stat>
            <Stat><StatValue>24/7</StatValue><StatLabel>Project discovery</StatLabel></Stat>
            <Stat><StatValue>4.9</StatValue><StatLabel>Community trust score</StatLabel></Stat>
          </StatsGrid>
        </Container>
      </StatsBand>

      <Section>
        <Container>
          <Split>
            <div>
              <SectionTitle>What we believe.</SectionTitle>
              <Body>
                Great creative work should not depend on who already knows whom.
                It should be easier to find a dependable camera package, a
                specialist crew, a production partner, or a verified studio when
                the brief is ready to move.
              </Body>
              <Button $primary onClick={() => navigate("/marketplace/post-requirement")}>
                Start a Project <CheckCircle2 size={15} />
              </Button>
            </div>
          </Split>
        </Container>
      </Section>
    </Page>
  );
};

export default MarketplaceAboutUsPage;
